import { useEffect, useId, useRef, useState } from 'react'
import type { PointerEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '../Icon/Icon'
import { cx } from '../utils/cx'
import type { SignaturePadProps } from './types'
import './SignaturePad.css'

/** Zona de firma (bundle B2): 160 px, borde discontinuo, línea de apoyo, "Borrar y repetir". Exporta PNG como la app actual. */
export function SignaturePad({ label, onChange, error, className }: SignaturePadProps) {
  const { t } = useTranslation()
  const uid = useId()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const drawing = useRef(false)
  const [empty, setEmpty] = useState(true)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const resize = () => {
      const ratio = window.devicePixelRatio || 1
      const { width, height } = canvas.getBoundingClientRect()
      if (width === 0 || height === 0) return
      canvas.width = Math.round(width * ratio)
      canvas.height = Math.round(height * ratio)
      const ctx = canvas.getContext('2d')
      if (ctx) {
        ctx.scale(ratio, ratio)
        ctx.lineWidth = 2.5
        ctx.lineCap = 'round'
        ctx.lineJoin = 'round'
        ctx.strokeStyle = '#1F1B16'
      }
    }
    resize()
    window.addEventListener('resize', resize)
    return () => window.removeEventListener('resize', resize)
  }, [])

  const point = (e: PointerEvent<HTMLCanvasElement>) => {
    const rect = e.currentTarget.getBoundingClientRect()
    return { x: e.clientX - rect.left, y: e.clientY - rect.top }
  }

  const start = (e: PointerEvent<HTMLCanvasElement>) => {
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    drawing.current = true
    e.currentTarget.setPointerCapture(e.pointerId)
    const { x, y } = point(e)
    ctx.beginPath()
    ctx.moveTo(x, y)
    setEmpty(false)
  }
  const move = (e: PointerEvent<HTMLCanvasElement>) => {
    if (!drawing.current) return
    const ctx = canvasRef.current?.getContext('2d')
    if (!ctx) return
    const { x, y } = point(e)
    ctx.lineTo(x, y)
    ctx.stroke()
  }
  const end = () => {
    if (!drawing.current) return
    drawing.current = false
    const canvas = canvasRef.current
    if (canvas) onChange?.(canvas.toDataURL('image/png'))
  }
  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas?.getContext('2d')
    if (canvas && ctx) ctx.clearRect(0, 0, canvas.width, canvas.height)
    setEmpty(true)
    onChange?.(null)
  }

  const labelId = `hp-sign-${uid}-label`
  const errorId = `hp-sign-${uid}-error`
  return (
    <div className={cx('hp-sign', error && 'hp-sign--error', className)}>
      <span id={labelId} className="hp-sign__label">
        {label ?? t('v2.ds.signature.label')}
      </span>
      <div className="hp-sign__area">
        <canvas
          ref={canvasRef}
          aria-labelledby={labelId}
          aria-describedby={error ? errorId : undefined}
          className="hp-sign__canvas"
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerCancel={end}
          onPointerLeave={end}
        />
        <span className="hp-sign__baseline" aria-hidden="true" />
        {empty && (
          <span className="hp-sign__placeholder" aria-hidden="true">
            {t('v2.ds.signature.placeholder')}
          </span>
        )}
      </div>
      <div className="hp-sign__foot">
        {error ? (
          <span id={errorId} className="hp-sign__error" role="alert">
            <Icon name="alert-triangle" size={18} />
            {error}
          </span>
        ) : (
          <span />
        )}
        <button type="button" onClick={clear} className="hp-sign__clear">
          {t('v2.ds.signature.borrar')}
        </button>
      </div>
    </div>
  )
}
